import _filter from "lodash/filter"
import { useEffect, useState } from "react"
import _map from "lodash/map"
import PageHeader from "components/layout/page-header"
import LoadingSkeleton from "./components/loading-skeleton"
import { Card } from "components/base"
import { useProjectsFilterFormik } from "../shared/hooks/use-projects-filters-formik"
import { useQueryParams } from "hooks/app-router"
import ProjectsTable from "./components/projects-table"
import { SearchFilter } from "./components/project-filters/search-filter"
import { StateFilter } from "./components/project-filters/state-filter"
import { Button } from "antd"
import { NewProjectSvg as NewProject } from "assets/svg/icon"
import { IntegrationSvg as Integration } from "assets/svg/icon"
import { useHistory } from "react-router-dom"
import useApiClient from "hooks/useApiClient"
import { usePagePermissions } from "hooks/permissions.hooks"
import ClientsFilter from "components/timenotes/filters/clients-filter"
import TeamFilter from "components/timenotes/filters/team-filter"
import BillableFilter from "components/timenotes/filters/billable-filter"
import useNoInitialEffect from "hooks/useNoInitialEffect"
import {
  IGetProjectsListParams,
  TApiProjectsSorting,
} from "@timenotes/shared/src/services/api-client/projects-api"
import { TApiProject } from "@timenotes/shared/src/services/api-client/types"
import useEnsureUserCanDisplayPage from "services/pages/use-ensure-user-can-display-page"
import { useProjectsFiltersQueryParams } from "../shared/hooks/use-projects-filters-query-params"

const PROJECTS_LIST_PER_PAGE = 30

const ProjectsListPage = () => {
  const [pageLoaded, setPageLoaded] = useState(false)

  useEnsureUserCanDisplayPage("projects")

  const apiClient = useApiClient()
  const formik = useProjectsFilterFormik()

  const [sorting, setSorting] = useState<TApiProjectsSorting>({ name: "asc" })
  const [nameFilter, setNameFilter] = useState<string | undefined>(undefined)
  const [stateFilter, setStateFilter] = useState<
    "active" | "archived" | undefined
  >("active")
  const [billableFilter, setBillableFilter] = useState<boolean | undefined>(
    undefined
  )
  const [clientHashIds, setClientHashIds] = useState<string[] | undefined>([])
  const [membersGroupHashIds, setMembersGroupHashIds] = useState<
    string[] | undefined
  >([])
  const [usersAccountHashIds, setUsersAccountHashIds] = useState<
    string[] | undefined
  >([])

  const [projects, setProjects] = useState<TApiProject[]>([])
  const [noFilters, setNoFilters] = useState<boolean>(true)
  const [currentPage, setCurrentPage] = useState(1)

  const [pagination, setPagination] = useState<TApiPagination>({
    currentPage: currentPage,
    pageSize: PROJECTS_LIST_PER_PAGE,
    totalPages: null,
    totalCount: null,
  })

  const queryParams = useQueryParams<{
    name?: string
    state?: "active" | "archived" | "all"
    billable?: "yes" | "no" | undefined
    groups?: string
    users?: string
    clients?: string
  }>()

  const qParams = useProjectsFiltersQueryParams()

  useEffect(() => {
    if (formik.dirty) {
      setNoFilters(false)
    } else {
      setNoFilters(true)
    }
  }, [formik.values])

  useEffect(() => {
    formik.setValues({
      name: queryParams.name ?? formik.values.name,
      state: queryParams.state ?? formik.values.state,
      billable: queryParams.billable ?? formik.values.billable,
      users: queryParams.users
        ? JSON.parse(queryParams.users as string)
        : formik.values.users,
      groups: queryParams.groups
        ? JSON.parse(queryParams.groups as string)
        : formik.values.groups,
      clients: queryParams.clients
        ? JSON.parse(queryParams.clients as string)
        : formik.values.clients,
    })
  }, [])

  useNoInitialEffect(() => {
    qParams.update(formik.values)
  }, [formik.values])

  const [listParams, setListParams] = useState<IGetProjectsListParams>({
    page: currentPage,
    perPage: PROJECTS_LIST_PER_PAGE,
    sorting: sorting,
    filters: {
      clientHashIds: clientHashIds,
      membersGroupHashIds: membersGroupHashIds,
      state: stateFilter || undefined,
      usersAccountHashIds: usersAccountHashIds,
      isBillable: billableFilter,
      name: nameFilter,
    },
  })

  useEffect(() => {
    setListParams({
      page: 1, // if filters changes - reset pagination to the first page as others might not exist!
      perPage: PROJECTS_LIST_PER_PAGE,
      sorting: sorting,
      filters: {
        clientHashIds: formik.values.clients,
        membersGroupHashIds: formik.values.groups,
        state: formik.values.state,
        usersAccountHashIds: formik.values.users,
        isBillable: formik.values.billable,
        name: formik.values.name,
      },
    })
  }, [formik.values])

  useEffect(() => {
    setListParams({
      page: currentPage,
      perPage: PROJECTS_LIST_PER_PAGE,
      sorting: sorting,
      filters: {
        clientHashIds: formik.values.clients,
        membersGroupHashIds: formik.values.groups,
        state: formik.values.state,
        usersAccountHashIds: formik.values.users,
        isBillable: formik.values.billable,
        name: formik.values.name,
      },
    })
  }, [currentPage, sorting])

  const fetchProjectsList = (updatePagination = false) => {
    apiClient.getProjectsList(listParams).then((response) => {
      setPageLoaded(true)
      setProjects(response.projects)

      // it goes as update as API does not return pageSize
      setPagination((oldPagination) => {
        return {
          ...oldPagination,
          ...response.meta.pagination,
        }
      })
    })
  }

  useEffect(() => {
    fetchProjectsList()
  }, [listParams])

  useNoInitialEffect(() => {
    fetchProjectsList()
  }, [])

  const history = useHistory()
  const { permissions } = usePagePermissions()

  const filtersData = listParams.filters
  const canCreateProject = permissions.create
  const canSetTimeLogBillable = permissions.setTimeLogBillable

  const goToNewProjectPath = () => {
    history.push("/projects/new")
  }

  const goToProjectPath = (projectId: string) => {
    if (projectId == null) {
    } else {
      return history.push(`/projects/${projectId}`)
    }
  }

  const goToNewIntegrationProjectPath = () => {
    history.push(`/integrations/new`)
  }

  function clearFilters() {
    history.replace(history.location.pathname)
    formik.resetForm()
    setNoFilters(true)
  }

  return (
    <div className="page-content">
      <PageHeader
        title="Projects"
        buttons={[
          {
            title: "Import data",
            type: "default",
            callback: () => {
              history.push("/imports/new")
            },
          },
          {
            title: "Add project",
            type: "primary",
            disabled: !canCreateProject,
            disabledTooltip: "You have no permissions to add a new project!",
            options: [
              {
                title: "New project",
                Icon: NewProject,
                callback: () => {
                  return goToNewProjectPath()
                },
              },
              {
                title: "From integration",
                Icon: Integration,
                callback: () => {
                  return goToNewIntegrationProjectPath()
                },
              },
            ],
          },
        ]}
      />

      <Card>
        {(() => {
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: ".5rem",
              }}
            >
              <SearchFilter
                initialValue={formik.values.name}
                placeholder={"Filter projects"}
                setSearchQuery={(search) =>
                  formik.setFieldValue("name", search)
                }
              />

              <StateFilter
                style={{ margin: 0 }}
                value={formik.values.state as "active" | "archived" | "all"}
                onChange={(value) => {
                  if (!value) {
                    formik.setFieldValue("state", "all")
                  } else {
                    formik.setFieldValue("state", value)
                  }
                }}
              />

              <ClientsFilter
                values={formik.values.clients}
                onChange={(values) => {
                  formik.setFieldValue("clients", values)
                }}
              />
              <TeamFilter
                values={{
                  memberHashIds: formik.values.users || [],
                  groupHashIds: formik.values.groups || [],
                }}
                onChange={(values) => {
                  formik.setFieldValue("users", values.memberHashIds)
                  formik.setFieldValue("groups", values.groupHashIds)
                }}
                width={"100%"}
              />
              {canSetTimeLogBillable && (
                <BillableFilter
                  style={{
                    width: "180px",
                  }}
                  value={
                    formik.values.billable === undefined
                      ? undefined
                      : formik.values.billable
                      ? "billable"
                      : "non-billable"
                  }
                  onChange={(value) => {
                    let newValue: boolean | undefined

                    switch (value) {
                      case undefined:
                        newValue = undefined
                        break
                      case "billable":
                        newValue = true
                        break
                      case "non-billable":
                        newValue = false
                        break
                    }

                    formik.setFieldValue("billable", newValue)
                  }}
                />
              )}
              {!noFilters && (
                <Button
                  onClick={clearFilters}
                  type="text"
                  style={{ color: "gray" }}
                >
                  Χ Clear filters
                </Button>
              )}
            </div>
          )
        })()}
      </Card>

      {pageLoaded ? (
        <ProjectsTable
          goToProjectPath={goToProjectPath}
          pagination={pagination}
          projects={projects}
          sorting={sorting}
          onCurrentPageChange={setCurrentPage}
          onSortingChange={setSorting}
          onReload={fetchProjectsList}
        />
      ) : (
        <LoadingSkeleton />
      )}
    </div>
  )
}

export default ProjectsListPage
